/**
 * Botão de gravação de áudio para o chat
 * 
 * Estados:
 * - Loading: aguardando inicialização do gravador
 * - Idle: ícone de microfone
 * - Recording: timer + botão de parar + cancelar
 * - Sending: loading spinner
 * - Blocked: provider não suporta áudio (ex: Instagram temporariamente)
 */

import { useState } from 'react';
import { Mic, Square, X, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useAudioRecorder, formatAudioDuration } from '@/hooks/useAudioRecorder';
import { toast } from 'sonner';

/**
 * Providers que temporariamente não suportam envio de áudio
 * Instagram: API da Meta não aceita formato OGG/Opus (gravado pelo navegador)
 * TODO: Remover quando conversão server-side for implementada
 */
const AUDIO_BLOCKED_PROVIDERS = ['meta-instagram'];

interface AudioRecorderButtonProps {
  // Callback quando áudio é gravado e pronto para envio
  onSend: (audioBlob: Blob, duration: number) => Promise<void>;
  // Se está desabilitado (conversa fechada)
  disabled?: boolean;
  // Slug do provider da inbox para verificar suporte a áudio
  providerSlug?: string | null;
}

export function AudioRecorderButton({ onSend, disabled, providerSlug }: AudioRecorderButtonProps) {
  const [isSending, setIsSending] = useState(false);
  
  const {
    isRecording,
    duration,
    startRecording,
    stopRecording,
    cancelRecording,
    error,
    isReady,
    initFailed,
  } = useAudioRecorder();

  // Verifica se o provider atual bloqueia envio de áudio
  const isAudioBlocked = providerSlug ? AUDIO_BLOCKED_PROVIDERS.includes(providerSlug) : false;

  const handleStartRecording = async () => {
    if (disabled || !isReady) return;
    
    const success = await startRecording();
    if (!success && !error) {
      toast.error('Não foi possível iniciar a gravação. Verifique as permissões do microfone.');
    }
  };

  /**
   * Para gravação e dispara envio em background (fire-and-forget)
   * Libera UI rapidamente para permitir gravações em sequência
   */
  const handleStopAndSend = async () => {
    const blob = await stopRecording();
    if (!blob) {
      toast.error('Erro ao finalizar gravação');
      return;
    }

    // Verifica duração mínima (1 segundo)
    if (duration < 1) {
      toast.error('Áudio muito curto. Grave pelo menos 1 segundo.');
      return;
    }

    setIsSending(true);
    
    // Fire-and-forget: dispara envio em background sem aguardar
    // Isso permite que o usuário grave múltiplos áudios em sequência
    onSend(blob, duration).catch(err => {
      console.error('[AudioRecorderButton] Erro em background ao enviar áudio:', err);
      toast.error('Erro ao enviar áudio');
    });
    
    // Libera UI após breve feedback visual (500ms)
    // O áudio aparecerá no chat como "pending" e atualizará para "sent" quando completar
    setTimeout(() => {
      setIsSending(false);
    }, 500);
  };

  const handleCancel = () => {
    cancelRecording();
  };

  // Estado: Enviando
  if (isSending) {
    return (
      <Button 
        variant="ghost" 
        size="icon"
        disabled
        className="shrink-0"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
      </Button>
    );
  }

  // Estado: Gravando
  if (isRecording) {
    return (
      <div className="flex items-center gap-2 animate-in fade-in slide-in-from-right-2">
        {/* Botão Cancelar */}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleCancel}
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
          title="Cancelar gravação"
        >
          <X className="h-4 w-4" />
        </Button>

        {/* Timer com indicador de gravação */}
        <div className="flex items-center gap-2 px-3 py-1.5 bg-destructive/10 rounded-full">
          <div className="h-2 w-2 rounded-full bg-destructive animate-pulse" />
          <span className="text-sm font-medium text-destructive tabular-nums">
            {formatAudioDuration(duration)}
          </span>
        </div>

        {/* Botão Parar/Enviar */}
        <Button
          variant="default"
          size="icon"
          onClick={handleStopAndSend}
          className="h-9 w-9 bg-primary hover:bg-primary/90"
          title="Parar e enviar"
        >
          <Square className="h-4 w-4 fill-current" />
        </Button>
      </div>
    );
  }

  // Estado: Falha na inicialização do gravador - exibe ícone de reload sutil
  if (initFailed) {
    return (
      <div className="flex items-center gap-1">
        {/* Microfone desabilitado */}
        <Button
          variant="ghost"
          size="icon"
          disabled
          className="shrink-0 opacity-50 cursor-not-allowed"
        >
          <Mic className="h-4 w-4" />
        </Button>
        
        {/* Ícone de reload clicável com tooltip */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => window.location.reload()}
                className="h-6 w-6 text-muted-foreground hover:text-primary transition-colors"
              >
                <RefreshCw className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">Recarregar para habilitar áudio</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  // Estado: Bloqueado por provider (ex: Instagram)
  if (isAudioBlocked) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              disabled
              className="shrink-0 opacity-50 cursor-not-allowed"
            >
              <Mic className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[200px] text-center">
            <p className="text-xs">
              Envio de áudio em breve para Instagram
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Estado: Idle - Botão de microfone
  // Desabilitado se não está pronto ou se a conversa está fechada
  const isButtonDisabled = disabled || !isReady;
  
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleStartRecording}
      disabled={isButtonDisabled}
      className={cn(
        'h-9 w-9 shrink-0 text-foreground hover:bg-primary/10 hover:text-primary transition-colors',
        isButtonDisabled && 'opacity-50 cursor-not-allowed'
      )}
      title={!isReady ? 'Preparando gravador...' : 'Gravar áudio'}
    >
      <Mic className="h-5 w-5" />
    </Button>
  );
}
